import { type License, type Qualification } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  ALL_WEEKDAYS,
  ALL_WEEKEND_DAYS,
  type DayOfTheWeek,
  isDayOfWeek,
} from "@src/appV2/lib/dates/types";
import { differenceInYears } from "date-fns";
import { isEmpty } from "lodash";

import { type WorkerApplicationPreferenceDetails } from "../Placements/api/useCreatePlacementApplication";
import { PAY_AND_SCHEDULE_FORM_MAX_YEARS_OF_EXPERIENCE } from "../Placements/components/PayAndScheduleFormV2/PayAndScheduleFormV2";
import { type ApplicationPreferencesFormData } from "../Placements/components/PlacementApplicationPreferenceForm/WorkerPreferencesStep";
import { isJobType, isShiftNameType, type JobType, type ShiftNameType } from "./types";

export function getDayPreference(
  preferredDaysOfWeek?: DayOfTheWeek[]
): "any" | "weekdays" | "weekends" | undefined {
  let dayPreference: "any" | "weekdays" | "weekends" | undefined;
  if (isDefined(preferredDaysOfWeek) && preferredDaysOfWeek.length > 0) {
    const candidateDays = new Set<DayOfTheWeek>(preferredDaysOfWeek);

    const hasAllWeekdays = ALL_WEEKDAYS.every((d) => candidateDays.has(d));
    const hasAllWeekendDays = ALL_WEEKEND_DAYS.every((d) => candidateDays.has(d));

    if (hasAllWeekdays && hasAllWeekendDays) {
      dayPreference = "any";
    } else if (hasAllWeekdays && ALL_WEEKEND_DAYS.every((d) => !candidateDays.has(d))) {
      dayPreference = "weekdays";
    } else if (hasAllWeekendDays && ALL_WEEKDAYS.every((d) => !candidateDays.has(d))) {
      dayPreference = "weekends";
    }
  } else {
    dayPreference = undefined;
  }

  return dayPreference;
}

export function getDayPreferenceLabel(preferredDaysOfWeek?: DayOfTheWeek[]): string | undefined {
  const dayPreference = getDayPreference(preferredDaysOfWeek);
  // FIXME: Automatically disabled during ESLint config update
  switch (dayPreference) {
    case "any": {
      return "All";
    }

    case "weekdays": {
      return "Weekdays";
    }

    case "weekends": {
      return "Weekends";
    }

    default: {
      return undefined;
    }
  }
}

export function getPreferredDaysOfWeek(
  dayPreference: "any" | "weekdays" | "weekends" | undefined
): DayOfTheWeek[] | undefined {
  // FIXME: Automatically disabled during ESLint config update
  switch (dayPreference) {
    case "any": {
      return [...ALL_WEEKDAYS, ...ALL_WEEKEND_DAYS];
    }

    case "weekdays": {
      return [...ALL_WEEKDAYS];
    }

    case "weekends": {
      return [...ALL_WEEKEND_DAYS];
    }

    default: {
      return undefined;
    }
  }
}

export function augmentYearsOfExperienceWithWorkerLicenses(
  yearsOfExperienceByQualification: Record<string, number | undefined> | undefined,
  workerLicenses: License[]
): Record<string, number | undefined> {
  const yearsOfExperienceFromLicenses = workerLicenses.reduce<Record<string, number | undefined>>(
    (accumulator, license) => {
      const { issueDate, status, qualification } = license.attributes;

      if (status !== "REJECTED" && isDefined(issueDate)) {
        const yearsOfExperienceFromLicense = Math.max(
          Math.min(
            Math.ceil(differenceInYears(new Date(), new Date(issueDate))),
            PAY_AND_SCHEDULE_FORM_MAX_YEARS_OF_EXPERIENCE
          ),
          0
        );

        accumulator[qualification] = Math.max(
          yearsOfExperienceFromLicense,
          accumulator[qualification] ?? 0
        );
      }

      return accumulator;
    },
    {}
  );

  return {
    ...yearsOfExperienceFromLicenses,
    // prioritize the years of experience entered manually by the candidate in the pay form
    ...yearsOfExperienceByQualification,
  };
}

// Utility function to safely split a delimited string into a strongly-typed array.
export function splitAndFilter<T extends string>(
  value: string | undefined,
  delimiter: string,
  guard: (v: string) => v is T
): T[] {
  if (!value) {
    return [];
  }

  return value
    .split(delimiter)
    .map((v) => v.trim())
    .filter((v): v is T => guard(v));
}

// Convert the form data captured in the PlacementApplicationPreferenceFormDialog into the API-ready shape expected by
// `workerApplicationPreferenceDetails`.
export function mapFormDataToPreferenceDetails(
  data: ApplicationPreferencesFormData
): WorkerApplicationPreferenceDetails {
  return {
    jobTypesInterested: splitAndFilter<JobType>(data.jobType, ",", isJobType),
    shiftTypesInterested: splitAndFilter<ShiftNameType>(data.shiftTime, ",", isShiftNameType),
    yearsOfExperienceByQualification: data.yearsOfExperience,
    notes: data.additionalNotes,
    preferredDaysOfWeek: splitAndFilter<DayOfTheWeek>(data.daysOfWeek, ",", isDayOfWeek),
  };
}

// Build comma-separated string of worker qualification names based on available types and licenses
export function getQualificationsString(params: {
  workerTypesAvailableForHiring: string[] | undefined;
  allWorkerQualifications: Qualification[] | undefined;
  licenseQualificationNames: string[];
  shoppableQualificationIds: string[];
}): string {
  const {
    workerTypesAvailableForHiring,
    allWorkerQualifications,
    licenseQualificationNames,
    shoppableQualificationIds,
  } = params;

  const hasAvailableTypesWithPlacements =
    workerTypesAvailableForHiring && workerTypesAvailableForHiring.length > 0;

  if (!isDefined(allWorkerQualifications) || isEmpty(allWorkerQualifications)) {
    return workerTypesAvailableForHiring?.join(",") ?? "";
  }

  const shouldIncludeShoppableQualifications =
    isEmpty(licenseQualificationNames) ||
    !workerTypesAvailableForHiring ||
    workerTypesAvailableForHiring.length === 0 ||
    !licenseQualificationNames.some((qualification) =>
      workerTypesAvailableForHiring.includes(qualification)
    );

  let filteredQualifications = allWorkerQualifications;
  if (!shouldIncludeShoppableQualifications) {
    const shoppableQualificationIdsSet = new Set(shoppableQualificationIds);
    const licenseQualificationNamesSet = new Set(licenseQualificationNames);

    filteredQualifications = allWorkerQualifications.filter(
      (qualification) =>
        !shoppableQualificationIdsSet.has(qualification.id) ||
        licenseQualificationNamesSet.has(qualification.attributes.name)
    );
  }

  const workerqualificationNames = filteredQualifications.map((q) => q.attributes.name);

  // If we have specific types available for hiring, filter to only those
  if (hasAvailableTypesWithPlacements) {
    const filteredNames = workerqualificationNames.filter((name) =>
      workerTypesAvailableForHiring.includes(name)
    );
    return filteredNames.length > 0
      ? filteredNames.join(",")
      : workerTypesAvailableForHiring.join(",");
  }

  return workerqualificationNames.join(",");
}
